import {
  UseMutationResult,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../SessionProvider';
import { LegendColor, mapLegendColors, mapSetLegendItems } from './mapLegend';
import { useSaveSettings } from './useSettings';
import { Settings } from './settings';
import { Session } from '@ais-ui/auth';
import {
  useTrackedMutation as useMutation,
  useTrackedQuery as useQuery,
} from '@ais-ui/performance-tracking';

export const useMapLegendColors = (): UseQueryResult<LegendColor[]> => {
  const { olSession, sessionId } = useSession();

  return useQuery(
    {
      queryKey: ['mapLegendColors'],
      queryFn: () => {
        return mapLegendColors(olSession);
      },
      enabled: !!sessionId,
    },
    'mapLegend:colors',
  );
};

export type SetMapLegendItemsArgs = {
  mapLegendItems: string[];
  showItem: boolean;
};

export const useSetMapLegendItems = (
  isReplayPlaying = false,
): UseMutationResult<null, Error, SetMapLegendItemsArgs, null> => {
  const { olSession, replaySession } = useSession();
  const queryClient = useQueryClient();

  return useMutation(
    {
      mutationFn: async ({
        mapLegendItems,
        showItem,
      }: SetMapLegendItemsArgs) => {
        const MAX_RETRIES = 5;
        const BASE_DELAY = 1000; // 1 second

        // We have to retry because the backend isn't always ready to accept the request
        //  and in that case gives an internal error back. But it's based on some intern map state
        //  that we can't really track on the frontend
        const executeWithRetry = async (
          session: Session,
          retryCount = 0,
        ): Promise<null> => {
          try {
            return await mapSetLegendItems(session, mapLegendItems, showItem);
          } catch (error) {
            if (retryCount < MAX_RETRIES) {
              // Exponential backoff with jitter
              const delay =
                BASE_DELAY *
                Math.pow(1.5, retryCount) *
                (0.9 + Math.random() * 0.2);
              console.warn(
                `mapSetLegendItems failed, retrying in ${Math.round(
                  delay,
                )}ms (attempt ${retryCount + 1}/${MAX_RETRIES})`,
              );

              return new Promise((resolve) => {
                setTimeout(() => {
                  resolve(executeWithRetry(session, retryCount + 1));
                }, delay);
              });
            }
            throw error;
          }
        };

        if (replaySession && isReplayPlaying) {
          await executeWithRetry(replaySession);
        }

        return executeWithRetry(olSession);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['getSettings'] }); // Information about shown items are in settings
        queryClient.invalidateQueries({ queryKey: ['mapLegendColors'] });
      },
    },
    'map_legend:set_items',
  );
};

export const useSaveMapLegendsToSettings = (isReplayPlaying = false) => {
  const saveSettingsMutation = useSaveSettings();
  const setMapLegendItemsMutation = useSetMapLegendItems(isReplayPlaying);

  return useMutation(
    {
      mutationFn: async ({
        settings,
        mapLegendItemsArgs,
      }: {
        settings: Settings;
        mapLegendItemsArgs?: SetMapLegendItemsArgs;
      }) => {
        // First, save the settings
        await saveSettingsMutation.mutateAsync(settings);

        if (!mapLegendItemsArgs) {
          mapLegendItemsArgs = {
            mapLegendItems:
              settings['ribbonPlugin.settings']?.ship_color_noshow_sel || [],
            showItem: false,
          };
        }

        // Then, set the map legend items
        await setMapLegendItemsMutation.mutateAsync(mapLegendItemsArgs);
      },
      onError: (error, _variables, _context) => {
        console.error(
          'Error in saveSettingsAndSetMapLegendItems mutation:',
          error,
        );
      },
    },
    'map_legend:save_to_settings',
  );
};
